import * as countries from 'i18n-iso-countries';

/**
 * Get country name for a given country code in a specific language
 * @param {string} countryCode - The ISO country code (e.g., 'US', 'DE', 'FR')
 * @param {string} language - The language code (e.g., 'en', 'de', 'fr', 'it', 'es', 'ca')
 * @returns {Promise<string>} The country name in the specified language
 */
export async function getCountryNameForCode(countryCode, language) {
	try {
		// Dynamic import of country translation data based on language
		let countryData;

		switch (language) {
		case 'de':
			countryData = await import('i18n-iso-countries/langs/de.json');
			break;
		case 'fr':
			countryData = await import('i18n-iso-countries/langs/fr.json');
			break;
		case 'it':
			countryData = await import('i18n-iso-countries/langs/it.json');
			break;
		case 'es':
			countryData = await import('i18n-iso-countries/langs/es.json');
			break;
		case 'ca':
			countryData = await import('i18n-iso-countries/langs/ca.json');
			break;
		default:
			countryData = await import('i18n-iso-countries/langs/en.json');
			break;
		}

		// Register the locale with the countries library
		countries.registerLocale(countryData.default);

		// Get the country names for the specified language
		const countryNames = countries.getNames(language);

		// Return the country name for the given code, or fallback to English if not found
		return countryNames[countryCode] || countries.getName(countryCode, 'en') || countryCode;
	} catch (error) {
		console.error(`Error loading country translations for language: ${language}`, error);
		// Fallback to English if there's an error
		try {
			const englishData = await import('i18n-iso-countries/langs/en.json');
			countries.registerLocale(englishData.default);
			return countries.getName(countryCode, 'en') || countryCode;
		} catch (fallbackError) {
			console.error('Error loading fallback English translations', fallbackError);
			return countryCode;
		}
	}
}

/**
 * Get all country names in a specific language
 * @param {string} language - The language code (e.g., 'en', 'de', 'fr', 'it', 'es', 'ca')
 * @returns {Promise<Object>} Object with country codes as keys and country names as values
 */
export async function getAllCountryNames(language) {
	try {
		// Dynamic import of country translation data based on language
		let countryData;

		switch (language) {
		case 'de':
			countryData = await import('i18n-iso-countries/langs/de.json');
			break;
		case 'fr':
			countryData = await import('i18n-iso-countries/langs/fr.json');
			break;
		case 'it':
			countryData = await import('i18n-iso-countries/langs/it.json');
			break;
		case 'es':
			countryData = await import('i18n-iso-countries/langs/es.json');
			break;
		case 'ca':
			countryData = await import('i18n-iso-countries/langs/ca.json');
			break;
		default:
			countryData = await import('i18n-iso-countries/langs/en.json');
			break;
		}

		// Register the locale with the countries library
		countries.registerLocale(countryData.default);

		// Get all country names for the specified language
		return countries.getNames(language);
	} catch (error) {
		console.error(`Error loading country translations for language: ${language}`, error);
		// Fallback to English if there's an error
		try {
			const englishData = await import('i18n-iso-countries/langs/en.json');
			countries.registerLocale(englishData.default);
			return countries.getNames('en');
		} catch (fallbackError) {
			console.error('Error loading fallback English translations', fallbackError);
			return {};
		}
	}
}
